/**
 * PaylasimliBelgelerPanel.jsx — firmanın paylaşımlı personel belgeleri: SGK Hizmet Dökümü (96) ve
 * İSG Eğitim Katılım Listesi (98). Backlog #142. Bunlar firmanın "resmî olmayan firma belgeleri"dir:
 * firma yükler, birçok çalışanına dağıtır, dağıtılan her çalışanın SGK / İSG kutusunu doldurur.
 *
 * İki yerde kullanılır: firma tarafı Firma Kartı › Evraklar (`staff` yok) ve denetmen tarafı
 * Firma Profili İncele › Belgeler (`staff`). Fark yalnızca ÇALIŞAN BAŞINA KARAR: Onayla / Reddet /
 * Beklemede'ye al yalnızca personele görünür (firma girişi karar ucunda 403 alır). Yükleme, çalışan
 * ekleme/çıkarma ve belgeyi kaldırma iki tarafta da var (uçlar sahibi firma + personel için açık).
 *
 * İnceleme belgeyi değil ÇALIŞANI karara bağlar: 14 kişiye verilmiş, listede 12 adı olan bir dökümde
 * 12 onay + 2 ret çıkar; belgenin kendisi reddedilmez. Reddin sebebi çalışan başına saklanır.
 *
 * Servis: services/personelBelgeService.js (rehber §8). Mock yok — çağıran sayfa bu paneli yalnızca
 * canlı görünümde çizer.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, Input, Label, Modal, ModalBody, ModalFooter,
  ModalHeader, Spinner, Table,
} from "reactstrap";
import { ChevronDown, ChevronRight, FileText, Pencil, Plus, Trash2, UserPlus, UserMinus } from "lucide-react";
import PersonelSecici from "../Kisi/PersonelSecici";
import PaylasimliBelgeYukleModal from "./PaylasimliBelgeYukleModal";
import { PaylasimliAlanDuzenleModal, PaylasimliOnayModal } from "./PaylasimliBelgeKararParcalari";
import { FILE_APPROVAL_STATUS } from "../../services/projectService";
import {
  BELGE_TIP, PAYLASIMLI_TIPLER, addPaylasimliBelgeKisiler, decidePaylasimliBelge, donemYaz, fetchPaylasimliBelgeDosya,
  getPaylasimliBelgeler, onayGereksinimi, removePaylasimliBelgeKisi, retirePaylasimliBelge,
} from "../../services/personelBelgeService";
import { useTranslation } from "react-i18next";

const RED_SEBEBI_MAX = 500;
const trTarih = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join(".") : "—");
const adSoyad = (k) => `${k.ad ?? ""} ${k.soyad ?? ""}`.trim() || `#${k.personId}`;

function DurumRozeti({ durum }) {
  const info = FILE_APPROVAL_STATUS[durum] ?? FILE_APPROVAL_STATUS[0];
  return <Badge color={info.color}>{info.label}</Badge>;
}

export default function PaylasimliBelgelerPanel({ firmaId, staff = false }) {
  const { t: tx } = useTranslation("components");
  const [aktif, setAktif] = useState(1);
  const [belgeler, setBelgeler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null); // { color, text }
  const [reloadToken, setReloadToken] = useState(0);
  const [acik, setAcik] = useState(() => new Set());
  const [secim, setSecim] = useState({}); // belgeId → personId[]
  const [busy, setBusy] = useState(false);

  const [yukleModal, setYukleModal] = useState(null); // fileTypeId
  const [ekleModal, setEkleModal] = useState(null); // belge
  const [ekleSecim, setEkleSecim] = useState([]);
  const [kaldirModal, setKaldirModal] = useState(null); // belge
  const [retModal, setRetModal] = useState(null); // { belge, personIds }
  const [retSebebi, setRetSebebi] = useState("");
  // #297 / #268: onay belgenin tarihini ve (Hizmet Dökümü'nde) ayını da isteyebilir; ikisi de belgenin kendisinde, belgedeki herkes için.
  const [onayModal, setOnayModal] = useState(null); // { belge, personIds }
  const [duzenleModal, setDuzenleModal] = useState(null); // { belge, alan: "tarih" | "donem" }

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => {
    if (!firmaId) return undefined;
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const rows = await getPaylasimliBelgeler(firmaId, { aktif });
        if (!ignore) setBelgeler(rows);
      } catch (e) {
        if (!ignore) {
          setError(e.message);
          setBelgeler([]);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [firmaId, aktif, reloadToken]);

  function toggleAcik(belgeId) {
    setAcik((prev) => {
      const next = new Set(prev);
      if (next.has(belgeId)) next.delete(belgeId); else next.add(belgeId);
      return next;
    });
  }

  function toggleSecim(belgeId, personId) {
    setSecim((prev) => {
      const mevcut = new Set(prev[belgeId] ?? []);
      if (mevcut.has(personId)) mevcut.delete(personId); else mevcut.add(personId);
      return { ...prev, [belgeId]: [...mevcut] };
    });
  }

  function hepsiniSec(belge) {
    const hepsi = belge.kisiler.map((k) => k.personId);
    const tamam = (secim[belge.belgeId] ?? []).length === hepsi.length;
    setSecim((prev) => ({ ...prev, [belge.belgeId]: tamam ? [] : hepsi }));
  }

  async function dosyayiAc(belge) {
    setError(null);
    try {
      const { url } = await fetchPaylasimliBelgeDosya(firmaId, belge.belgeId);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(e.message);
    }
  }

  async function karar(belge, personIds, status, rejectReason, ek = {}) {
    setBusy(true);
    setError(null);
    try {
      await decidePaylasimliBelge(firmaId, belge.belgeId, { personIds, status, rejectReason, ...ek });
      setFlash({
        color: "success",
        text: tx("paylasimliBelgeler.calisan", { length: personIds.length, v: status === 1 ? tx("paylasimliBelgeler.onaylandi") : status === 2 ? tx("paylasimliBelgeler.reddedildi") : tx("paylasimliBelgeler.beklemedeYeAlindi") }),
      });
      setSecim((prev) => ({ ...prev, [belge.belgeId]: [] }));
      setRetModal(null);
      setOnayModal(null);
      setRetSebebi("");
      reload();
    } catch (e) {
      // Onay penceresi açıksa hata orada kalmasın: sayfanın üstünde görünür, pencere açık kalır ve değerler düzeltilebilir.
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  // Onay: belgede tarih (ya da Hizmet Dökümü'nde ay) eksikse ya da tarih geçmişse önce sorulur — backend aksi halde 400 verir (#295, #267);
  // ikisi de tamamsa eski gibi tek tık.
  function onayla(belge, personIds) {
    const g = onayGereksinimi(belge);
    if (g.tarihGerekli || g.donemGerekli) {
      setError(null);
      setOnayModal({ belge, personIds });
    } else {
      karar(belge, personIds, 1);
    }
  }

  async function kisiCikar(belge, kisi) {
    setBusy(true);
    setError(null);
    try {
      await removePaylasimliBelgeKisi(firmaId, belge.belgeId, kisi.personId);
      setFlash({ color: "success", text: tx("paylasimliBelgeler.belgedenCikarildi", { adSoyad: adSoyad(kisi) }) });
      reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function kisiEkle() {
    setBusy(true);
    setError(null);
    try {
      const sonuc = await addPaylasimliBelgeKisiler(firmaId, ekleModal.belgeId, ekleSecim);
      setFlash({
        color: "success",
        text: tx("paylasimliBelgeler.calisanEklendiBeklemede", { length: sonuc.eklenen.length })
          + (sonuc.zatenVar.length ? tx("paylasimliBelgeler.kisiZatenBelgede", { length: sonuc.zatenVar.length }) : "."),
      });
      setEkleModal(null);
      setEkleSecim([]);
      reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function belgeyiKaldir() {
    setBusy(true);
    setError(null);
    try {
      await retirePaylasimliBelge(firmaId, kaldirModal.belgeId);
      setFlash({ color: "success", text: tx("paylasimliBelgeler.belgeKaldirildi") });
      setKaldirModal(null);
      reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  function yuklemeBitti({ belge, rosterDisindaAtlananlar }) {
    const atlanan = rosterDisindaAtlananlar.length;
    setFlash({
      color: atlanan ? "warning" : "success",
      text: tx("paylasimliBelgeler.belgeYuklendiCalisanEklendiBeklemede", { v: belge?.kisiSayisi ?? 0 })
        + (atlanan ? tx("paylasimliBelgeler.kisiArtikPersonelListesindeOlmadigi", { atlanan }) : ""),
    });
    setYukleModal(null);
    reload();
  }

  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-2">
        <h6 className="mb-0">{tx("paylasimliBelgeler.paylasimliPersonelBelgeleri")}</h6>
        <Input
          type="select" bsSize="sm" style={{ width: 160 }} className="ms-auto" value={aktif}
          onChange={(e) => setAktif(Number(e.target.value))}
        >
          <option value={1}>{tx("paylasimliBelgeler.guncel")}</option>
          <option value={0}>{tx("paylasimliBelgeler.kaldirilmis")}</option>
        </Input>
      </div>

      {flash && (
        <Alert fade={false} color={flash.color} toggle={() => setFlash(null)} className="py-2 small">{flash.text}</Alert>
      )}
      {error && <Alert fade={false} color="danger" toggle={() => setError(null)} className="py-2 small">{error}</Alert>}

      {loading ? (
        <div className="text-center py-4"><Spinner size="sm" /></div>
      ) : (
        PAYLASIMLI_TIPLER.map((tip) => {
          const liste = belgeler.filter((b) => b.fileTypeId === tip.fileTypeId);
          return (
            <Card key={tip.fileTypeId} className="mb-3">
              <CardHeader className="bg-transparent d-flex align-items-center gap-2">
                <FileText size={16} className="text-muted" />
                <span className="fw-semibold">{tip.ad}</span>
                <span className="small text-muted">{liste.length}{" "}{tx("paylasimliBelgeler.belge")}</span>
                {aktif === 1 && (
                  <Button size="sm" color="primary" className="ms-auto" onClick={() => setYukleModal(tip.fileTypeId)}>
                    <Plus size={14} className="me-1" />{tx("paylasimliBelgeler.yeniBelgeYukle")}
                  </Button>
                )}
              </CardHeader>
              <CardBody className="p-0">
                {liste.length === 0 ? (
                  <div className="text-center text-muted small py-4">
                    {aktif === 1 ? tx("paylasimliBelgeler.henuzYuklenmisBelgeYok") : tx("paylasimliBelgeler.kaldirilmisBelgeYok")}
                  </div>
                ) : (
                  liste.map((belge) => {
                    const acikMi = acik.has(belge.belgeId);
                    const secilenler = secim[belge.belgeId] ?? [];
                    return (
                      <div key={belge.belgeId} className="border-top">
                        <div className="d-flex align-items-center gap-2 flex-wrap p-3">
                          <Button size="sm" color="light" onClick={() => toggleAcik(belge.belgeId)} aria-label={tx("paylasimliBelgeler.calisanlariGoster")}>
                            {acikMi ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </Button>
                          <div className="me-auto">
                            <div className="fw-medium">{belge.fileGivenName || belge.belgeTuru}</div>
                            <div className="small text-muted">
                              {tx("paylasimliBelgeler.yuklenmeSonGecerlilik", { trTarih: trTarih(belge.yuklemeTarihi), trTarih2: trTarih(belge.expiryDate) })}
                              {belge.expired && <Badge color="danger" className="ms-2 fw-normal">{tx("paylasimliBelgeler.suresiDoldu")}</Badge>}
                              {/* #297: firma tarihsiz yükler; tarihi onaylayan personel girer. Personel görür ve düzeltir, firma bilgi alır. */}
                              {!belge.expiryDate && belge.aktif && (staff
                                ? <Badge color="warning" className="ms-2 fw-normal">{tx("paylasimliBelgeler.tarihGirilmedi")}</Badge>
                                : <span className="ms-2">{tx("paylasimliBelgeler.tarihiFdsBelirler")}</span>)}
                              {staff && belge.aktif && (
                                <Button
                                  size="sm" color="light" className="ms-1 py-0 px-1" title={tx("paylasimliBelgeler.gecerlilikTarihiniDuzenle")}
                                  aria-label={tx("paylasimliBelgeler.gecerlilikTarihiniDuzenle")} onClick={() => setDuzenleModal({ belge, alan: "tarih" })}
                                >
                                  <Pencil size={12} />
                                </Button>
                              )}
                              {!belge.aktif && <Badge color="secondary" className="ms-2 fw-normal">{tx("paylasimliBelgeler.kaldirildi")}{" "}{trTarih(belge.kaldirmaTarihi)}</Badge>}
                            </div>
                            {/* #268: Hizmet Dökümü hangi ayı kapsıyor. "N ay eski" yalnızca bilgi — kartı hiçbir zaman engellemez (#267). */}
                            {belge.fileTypeId === BELGE_TIP.SGK_HIZMET_DOKUMU && (belge.belgeDonemi || (staff && belge.aktif)) && (
                              <div className="small text-muted">
                                {belge.belgeDonemi
                                  ? tx("paylasimliBelgeler.donem", { v: donemYaz(belge.belgeDonemi) })
                                  : <Badge color="warning" className="fw-normal">{tx("paylasimliBelgeler.donemGirilmedi")}</Badge>}
                                {belge.belgeDonemi && belge.belgeDonemiEski && (
                                  <Badge color="warning" className="ms-2 fw-normal">{tx("paylasimliBelgeler.ayEski", { length: belge.belgeDonemiAyFarki })}</Badge>
                                )}
                                {staff && belge.aktif && (
                                  <Button
                                    size="sm" color="light" className="ms-1 py-0 px-1" title={tx("paylasimliBelgeler.donemiDuzenle")}
                                    aria-label={tx("paylasimliBelgeler.donemiDuzenle")} onClick={() => setDuzenleModal({ belge, alan: "donem" })}
                                  >
                                    <Pencil size={12} />
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                          <span className="small">{belge.kisiSayisi}{" "}{tx("paylasimliBelgeler.calisan2")}</span>
                          <Badge color="warning" className="fw-normal">{belge.beklemede}{" "}{tx("paylasimliBelgeler.beklemede")}</Badge>
                          <Badge color="success" className="fw-normal">{belge.onaylandi}{" "}{tx("paylasimliBelgeler.onaylandi2")}</Badge>
                          <Badge color="danger" className="fw-normal">{belge.reddedildi}{" "}{tx("paylasimliBelgeler.reddedildi2")}</Badge>
                          <Button size="sm" color="light" onClick={() => dosyayiAc(belge)}>
                            <FileText size={13} className="me-1" />{tx("paylasimliBelgeler.ac")}
                          </Button>
                          {belge.aktif && (
                            <>
                              <Button size="sm" color="light" onClick={() => { setEkleSecim([]); setEkleModal(belge); }}>
                                <UserPlus size={13} className="me-1" />{tx("paylasimliBelgeler.calisanEkle")}
                              </Button>
                              <Button size="sm" color="danger" outline onClick={() => setKaldirModal(belge)} title={tx("paylasimliBelgeler.belgeyiKaldir")}>
                                <Trash2 size={13} />
                              </Button>
                            </>
                          )}
                        </div>

                        {acikMi && (
                          <div className="px-3 pb-3">
                            {/* Çubuk HEP durur (seçim yokken düğmeler pasif): ilk seçimde belirip satırları
                                aşağı itmesin, ikinci tık yanlış satıra düşmesin. */}
                            {staff && belge.aktif && belge.kisiler.length > 0 && (
                              <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                                <span className="small text-muted">{secilenler.length}{" "}{tx("paylasimliBelgeler.calisanSecili")}</span>
                                <Button size="sm" color="success" disabled={busy || secilenler.length === 0} onClick={() => onayla(belge, secilenler)}>{tx("paylasimliBelgeler.onayla")}</Button>
                                <Button size="sm" color="danger" outline disabled={busy || secilenler.length === 0} onClick={() => { setRetSebebi(""); setRetModal({ belge, personIds: secilenler }); }}>{tx("paylasimliBelgeler.reddet")}</Button>
                                <Button size="sm" color="light" disabled={busy || secilenler.length === 0} onClick={() => karar(belge, secilenler, 0)}>{tx("paylasimliBelgeler.beklemedeYeAl")}</Button>
                              </div>
                            )}
                            {belge.kisiler.length === 0 ? (
                              <div className="small text-muted">{tx("paylasimliBelgeler.buBelgeHenuzHicbirCalisana")}</div>
                            ) : (
                              <div className="table-responsive">
                                <Table size="sm" className="align-middle mb-0">
                                  <thead className="table-light">
                                    <tr>
                                      {staff && belge.aktif && (
                                        <th style={{ width: 34 }}>
                                          <Input
                                            type="checkbox" aria-label={tx("paylasimliBelgeler.tumunuSec")}
                                            checked={secilenler.length === belge.kisiler.length}
                                            onChange={() => hepsiniSec(belge)}
                                          />
                                        </th>
                                      )}
                                      <th>{tx("paylasimliBelgeler.calisan3")}</th>
                                      <th>{tx("paylasimliBelgeler.kimlikNo")}</th>
                                      <th>{tx("paylasimliBelgeler.durum")}</th>
                                      <th className="text-end">{tx("paylasimliBelgeler.islem")}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {belge.kisiler.map((k) => (
                                      <tr key={k.personId}>
                                        {staff && belge.aktif && (
                                          <td>
                                            <Input
                                              type="checkbox" aria-label={adSoyad(k)}
                                              checked={secilenler.includes(k.personId)}
                                              onChange={() => toggleSecim(belge.belgeId, k.personId)}
                                            />
                                          </td>
                                        )}
                                        <td>{adSoyad(k)}</td>
                                        <td className="font-monospace small">{k.tcNo || k.tcNoMasked || "—"}</td>
                                        <td>
                                          <DurumRozeti durum={k.durum} />
                                          {k.durum === 2 && k.redSebebi && (
                                            <div className="small text-danger fst-italic">“{k.redSebebi}”</div>
                                          )}
                                        </td>
                                        <td className="text-end text-nowrap">
                                          {staff && belge.aktif && (
                                            <>
                                              <Button
                                                size="sm" color="success" outline className="me-1"
                                                disabled={busy || k.durum === 1} onClick={() => onayla(belge, [k.personId])}
                                              >
                                                {tx("paylasimliBelgeler.onayla")}
                                              </Button>
                                              <Button
                                                size="sm" color="danger" outline className="me-1" disabled={busy}
                                                onClick={() => { setRetSebebi(""); setRetModal({ belge, personIds: [k.personId] }); }}
                                              >
                                                {tx("paylasimliBelgeler.reddet")}
                                              </Button>
                                              {k.durum !== 0 && (
                                                <Button size="sm" color="light" className="me-1" disabled={busy} onClick={() => karar(belge, [k.personId], 0)}>
                                                  {tx("paylasimliBelgeler.beklemede")}
                                                </Button>
                                              )}
                                            </>
                                          )}
                                          {belge.aktif && (
                                            <Button
                                              size="sm" color="light" disabled={busy} title={tx("paylasimliBelgeler.calisaniBelgedenCikar")}
                                              onClick={() => kisiCikar(belge, k)}
                                            >
                                              <UserMinus size={13} />
                                            </Button>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </CardBody>
            </Card>
          );
        })
      )}

      {yukleModal != null && (
        <PaylasimliBelgeYukleModal
          firmaId={firmaId}
          fileTypeId={yukleModal}
          staff={staff}
          oncekiBelgeler={belgeler.filter((b) => b.fileTypeId === yukleModal && b.aktif)}
          onClose={() => setYukleModal(null)}
          onDone={yuklemeBitti}
        />
      )}

      {onayModal && (
        <PaylasimliOnayModal
          belge={onayModal.belge} kisiSayisi={onayModal.personIds.length} busy={busy} error={error}
          onClose={() => { setOnayModal(null); setError(null); }}
          onConfirm={(ek) => karar(onayModal.belge, onayModal.personIds, 1, undefined, ek)}
        />
      )}

      {duzenleModal && (
        <PaylasimliAlanDuzenleModal
          firmaId={firmaId} belge={duzenleModal.belge} alan={duzenleModal.alan}
          onClose={() => setDuzenleModal(null)}
          onDone={(text) => { setDuzenleModal(null); setFlash({ color: "success", text }); reload(); }}
        />
      )}

      <Modal isOpen={!!ekleModal} toggle={busy ? undefined : () => setEkleModal(null)} size="lg" scrollable>
        <ModalHeader toggle={busy ? undefined : () => setEkleModal(null)}>
          {tx("paylasimliBelgeler.calisanEkle2", { v: ekleModal?.fileGivenName || ekleModal?.belgeTuru })}
        </ModalHeader>
        <ModalBody>
          {ekleModal && (
            <PersonelSecici
              firmaId={firmaId} secili={ekleSecim} onChange={setEkleSecim}
              devreDisi={ekleModal.kisiler.map((k) => k.personId)}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <span className="small text-muted me-auto">{tx("paylasimliBelgeler.eklenenHerkesBeklemedeBaslar")}</span>
          <Button color="light" onClick={() => setEkleModal(null)} disabled={busy}>{tx("paylasimliBelgeler.vazgec")}</Button>
          <Button color="primary" onClick={kisiEkle} disabled={busy || ekleSecim.length === 0}>
            {busy ? <Spinner size="sm" /> : tx("paylasimliBelgeler.ekle", { length: ekleSecim.length })}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!kaldirModal} toggle={busy ? undefined : () => setKaldirModal(null)}>
        <ModalHeader toggle={busy ? undefined : () => setKaldirModal(null)}>{tx("paylasimliBelgeler.belgeyiKaldir")}</ModalHeader>
        <ModalBody>
          {kaldirModal && (
            <>
              <p className="mb-2">
                <strong>{kaldirModal.fileGivenName || kaldirModal.belgeTuru}</strong>{" "}{tx("paylasimliBelgeler.kaldirilacak")}
              </p>
              <Alert fade={false} color="warning" className="small mb-0">
                {tx("paylasimliBelgeler.belgeUzerindeki")}{" "}<strong>{kaldirModal.kisiSayisi}{" "}{tx("paylasimliBelgeler.calisanIcinAyniAnda")}</strong>{" "}{tx("paylasimliBelgeler.sayilmazOlurBuCalisanlarin")}{" "}{kaldirModal.fileTypeId === 98 ? tx("paylasimliBelgeler.isg") : tx("paylasimliBelgeler.sgk")}{" "}{tx("paylasimliBelgeler.kutusuBaskaBirBelgeYoksa")}
              </Alert>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setKaldirModal(null)} disabled={busy}>{tx("paylasimliBelgeler.vazgec")}</Button>
          <Button color="danger" onClick={belgeyiKaldir} disabled={busy}>
            {busy ? <Spinner size="sm" /> : tx("paylasimliBelgeler.kaldir")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!retModal} toggle={busy ? undefined : () => setRetModal(null)}>
        <ModalHeader toggle={busy ? undefined : () => setRetModal(null)}>{tx("paylasimliBelgeler.calisaniReddet")}</ModalHeader>
        <ModalBody>
          {retModal && (
            <>
              <p className="mb-2 small">
                {retModal.personIds.length === 1
                  ? <><strong>{adSoyad(retModal.belge.kisiler.find((k) => k.personId === retModal.personIds[0]) ?? { personId: retModal.personIds[0] })}</strong>{" "}{tx("paylasimliBelgeler.reddedilecek")}</>
                  : <><strong>{retModal.personIds.length}{" "}{tx("paylasimliBelgeler.calisan2")}</strong>{" "}{tx("paylasimliBelgeler.reddedilecekSebepHerBirineAyri")}</>}
              </p>
              <Label className="small mb-1">{tx("paylasimliBelgeler.redSebebi")}</Label>
              <Input
                type="textarea" rows={3} value={retSebebi} invalid={retSebebi.length > RED_SEBEBI_MAX}
                onChange={(e) => setRetSebebi(e.target.value)} placeholder={tx("paylasimliBelgeler.ornListedeAdiYok")}
              />
              <div className="small text-muted mt-1">{retSebebi.length} / {RED_SEBEBI_MAX}</div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setRetModal(null)} disabled={busy}>{tx("paylasimliBelgeler.vazgec")}</Button>
          <Button
            color="danger" disabled={busy || !retSebebi.trim() || retSebebi.length > RED_SEBEBI_MAX}
            onClick={() => karar(retModal.belge, retModal.personIds, 2, retSebebi.trim())}
          >
            {busy ? <Spinner size="sm" /> : tx("paylasimliBelgeler.reddet")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
